import * as React from "react";
import classNames from "classnames";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Red from "@material-ui/core/colors/red";
import DialogActions from "@material-ui/core/DialogActions";
import { format } from "lib/globalize-util";
import { themeOptions } from "styles/theme";
import { useArchiveProduct } from "./hooks/product";
import CircularProgress from "material-ui/CircularProgress";

interface IProps {
    open: boolean;
    productId: string;
    onClose: () => void;
    onConfirm: () => void;
    onEntriesRefresh: () => void;
}

const ArchiveProductConfirmation: React.FC<IProps> = ({ open, productId, onClose, onConfirm, onEntriesRefresh }) => {
    const [shouldArchive, setShouldArchive] = React.useState(false);
    const { data, isLoading } = useArchiveProduct(productId, { enabled: shouldArchive });

    const titleClasses = useTitleStyles({});
    const classes = useStyles();

    React.useEffect(() => {
        if (data) {
            onEntriesRefresh();
            setShouldArchive(false);
            onClose();
            onConfirm();
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleArchiveProduct = React.useCallback(() => {
        setShouldArchive(true);
    }, []);

    return (
        <Dialog open={open} onClose={onClose} classes={{ root: classes.confirmationDialog }}>
            <DialogTitle className={titleClasses.dialogTitle} disableTypography={true}>
                <InfoOutlinedIcon fontSize="large" className={classes.infoIcon} />
                <span className={classes.titleText}>{format("Archive Product")}</span>
                <IconButton className={titleClasses.close} aria-label="close" onClick={onClose}>
                    <CloseIcon className={titleClasses.iconClose} />
                </IconButton>
            </DialogTitle>
            {isLoading ? (
                <CircularProgress style={{ display: "block", margin: "10px auto" }} />
            ) : (
                <DialogContent className={classes.content}>
                    <div className={classes.textContent}>
                        {format(
                            "Archiving will remove your product from your gallery entirely. Would you like to continue?",
                        )}
                    </div>
                </DialogContent>
            )}
            <DialogActions className={classes.actions} disableSpacing={true}>
                <Button classes={{ root: classes.button }} variant="outlined" onClick={onClose}>
                    {format("Back")}
                </Button>
                <Button
                    classes={{ root: classNames(classes.button, classes.confirm) }}
                    variant="contained"
                    disabled={isLoading}
                    onClick={handleArchiveProduct}
                    disableElevation={true}
                >
                    {format("Archive Product")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const useTitleStyles = makeStyles({
    dialogTitle: {
        fontSize: 16,
        fontWeight: "bold",
        borderBottom: `1px solid ${themeOptions.palette.lightGrey}`,
    },
    close: {
        position: "absolute",
        top: 5,
        right: 7,
    },
    iconClose: {
        width: 24,
        height: 24,
        color: themeOptions.palette.darkestGrey,
    },
});

const useStyles = makeStyles<Theme>({
    button: {
        height: 40,
        borderRadius: 20,
        fontSize: 13,
        fontFamily: "inherit",
        textTransform: "initial",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    confirm: {
        color: "white",
        marginLeft: 20,
        backgroundColor: themeOptions.palette.errorColor,
        "&:hover": {
            backgroundColor: Red[600],
        },
    },
    content: {
        padding: "24px 24px 40px",
        fontSize: "14px",
    },
    actions: {
        padding: 24,
    },
    infoIcon: {
        color: themeOptions.palette.errorColor,
        verticalAlign: "middle",
    },
    titleText: {
        paddingLeft: "10px",
        verticalAlign: "middle",
    },
    confirmationDialog: {
        zIndex: "1600 !important" as any,
    },
    textContent: {
        marginBottom: 20,
        fontSize: 12,
        color: themeOptions.palette.darkGrey,
    },
});

export default ArchiveProductConfirmation;
